import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { cloneDeep } from 'lodash'

import {
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Fab,
  Tooltip,
  FormLabel,
} from '@mui/material'

import { Add } from '@mui/icons-material'
import { addKeyToObjectsArray } from 'utils/addUniqueKeyToObjects'
import { formatToCNPJ } from 'utils/masks'
import { cnpjValid } from 'utils/validators'
import { hasAnswerChanged } from 'utils/functions'
import { OPERATION_TYPES } from 'utils/constants/operationTypes'

import styles from './styles'
import AnswerCard from '../AnswerCard'
import QuestionButton from '../QuestionButton'

export default function QuestionSix(props) {
  const { questionData, saveResponses } = props
  const classes = styles()

  const initialData = {
    corporate_name: '',
    cnpj: '',
    product_description: '',
  }

  const initialErrors = {
    corporate_name: false,
    cnpj: false,
    product_description: false,
  }

  const initializeFormState = (data) => {
    return {
      hasNonXPInternationalFinancialProducts: data.items.length > 0,
      items: data.items,
    }
  }

  const [formState, setFormState] = useState(initializeFormState(questionData))

  const [activityData, setActivityData] = useState(initialData)

  const [activityErrors, setActivityErrors] = useState(initialErrors)

  const [loading, setLoading] = useState(false)

  const [edited, setEdited] = useState(false)
  const [saved, setSaved] = useState(questionData.has_answer)

  const handleFieldChange = (event) => {
    const key = event.target.id
    const value =
      key === 'cnpj' ? formatToCNPJ(event.target.value) : event.target.value
    if (key === 'cnpj' && value?.length >= 19) {
      return
    }
    const newActivityData = cloneDeep(activityData)
    newActivityData[key] = value
    setActivityData(newActivityData)
  }

  const handleRadioChange = (event) => {
    const newFormState = cloneDeep(formState)
    newFormState.hasNonXPInternationalFinancialProducts =
      event.target.value === 'yes'
    setFormState(newFormState)
  }

  const hasNonXPInternationalFinancialProducts = () => {
    return !(formState.items.length === 0)
  }

  const validateAndSave = async (skipValidation) => {
    if (
      formState.items.length > 0 &&
      formState.hasNonXPInternationalFinancialProducts === false
    ) {
      toast.warn(
        'Você deve excluir as atividades antes de atualizar as respostas.',
      )
      return
    }
    const newQuestionData = cloneDeep(questionData)
    if (
      !skipValidation &&
      formState.hasNonXPInternationalFinancialProducts &&
      !hasNonXPInternationalFinancialProducts()
    ) {
      toast.error(
        'Ao selecionar "Sim" deve-se adicionar ao menos um produto financeiro',
      )
    } else {
      newQuestionData.has_answer = true
      newQuestionData.items = formState.items
      setLoading(true)
      await saveResponses(
        newQuestionData,
        'international_product_offering_activities',
        !saved ? OPERATION_TYPES.CREATE : OPERATION_TYPES.UPDATE,
      )
        .then(() => {
          setSaved(true)
          setEdited(false)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  const validateRequiredFields = () => {
    const newActivityErrors = initialErrors

    Object.keys(initialErrors).forEach((key) => {
      if (key === 'cnpj' && activityData[key] !== '') {
        newActivityErrors[key] = !cnpjValid(activityData[key])
      }
      if (activityData[key] === '') {
        newActivityErrors[key] = true
      }
    })

    setActivityErrors(newActivityErrors)

    return !Object.keys(newActivityErrors).some((key) => newActivityErrors[key])
  }

  const handleValidateAndUpdateState = () => {
    if (validateRequiredFields()) {
      const newFormState = cloneDeep(formState)
      newFormState.items.push(activityData)
      setFormState(newFormState)
      setActivityData(initialData)
    } else {
      toast.warn(
        'Favor, verifique se todos os campos obrigatórios foram preenchidos corretamente',
      )
    }
  }

  const handleRemoveProduct = (position) => {
    const newFormState = cloneDeep(formState)
    newFormState.items = newFormState.items.filter(
      (activity, index) => index !== position,
    )
    setFormState(newFormState)
  }

  const buildProduct = (productInfo) => {
    return (
      <React.Fragment>
        <p>
          <span>Razão social da instituição: </span>
          {productInfo.corporate_name}
        </p>
        <p>
          <span>CNPJ: </span>
          {formatToCNPJ(productInfo.cnpj)}
        </p>

        <p>
          <span>Descrição dos produtos oferecidos: </span>
          {productInfo.product_description}
        </p>
      </React.Fragment>
    )
  }

  useEffect(() => {
    const hasChanged =
      hasAnswerChanged(questionData.items, formState.items) ||
      formState.hasNonXPInternationalFinancialProducts !==
        questionData.items.length > 0
    setEdited(hasChanged)
    if (
      formState.items.length > 0 &&
      formState.hasNonXPInternationalFinancialProducts === false
    ) {
      toast.warn(
        'Você deve excluir as atividades antes de atualizar as respostas.',
      )
    }
  }, [formState])

  useEffect(() => {
    setSaved(questionData.has_answer)
    setFormState(initializeFormState(questionData))
  }, [questionData])

  useEffect(() => {
    if (hasAnswerChanged(questionData.items, formState.items))
      validateAndSave(true)
  }, [formState.items])

  return (
    <div className={classes.root}>
      <div className={classes.formContainer}>
        <h3>Questão 6</h3>
        <div className={classes.form}>
          <FormControl>
            <FormLabel>
              Você oferece produtos internacionais que não sejam distribuídos
              pela XP Inc?
            </FormLabel>
            <RadioGroup
              row
              value={
                formState.hasNonXPInternationalFinancialProducts ? 'yes' : 'no'
              }
              name="hasRelations"
              onChange={handleRadioChange}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Sim" />
              <FormControlLabel value="no" control={<Radio />} label="Não" />
            </RadioGroup>
          </FormControl>

          {formState.hasNonXPInternationalFinancialProducts ? (
            <div className={classes.formFields}>
              <TextField
                id="corporate_name"
                label="Razão Social da instituição"
                onChange={handleFieldChange}
                required
                fullWidth
                value={activityData.corporate_name}
                error={activityErrors.corporate_name}
                size="small"
              />

              <TextField
                id="cnpj"
                label="CNPJ"
                onChange={handleFieldChange}
                required
                value={activityData.cnpj}
                fullWidth
                error={activityErrors.cnpj}
                size="small"
              />

              <TextField
                id="product_description"
                label="Descrição dos produtos oferecidos"
                onChange={handleFieldChange}
                error={activityErrors.product_description}
                value={activityData.product_description}
                required
                fullWidth
                multiline
                size="small"
              />

              <div className={classes.fabContainer}>
                <Tooltip title="Adicionar atividade">
                  <Fab
                    onClick={handleValidateAndUpdateState}
                    disabled={formState?.items?.length === 10}
                    size="small"
                    color="primary"
                  >
                    <Add />
                  </Fab>
                </Tooltip>
              </div>
            </div>
          ) : null}
        </div>
        {formState?.items?.length > 0 ? (
          <div className={classes.answersContainer}>
            {addKeyToObjectsArray(formState.items)?.map((activity, index) => (
              <AnswerCard
                key={activity.key}
                index={index}
                handleRemove={handleRemoveProduct}
                mustRemove={
                  formState.hasNonXPInternationalFinancialProducts === false
                }
              >
                {buildProduct(activity)}
              </AnswerCard>
            ))}
          </div>
        ) : null}
      </div>
      <div className={classes.buttonsContainer}>
        <QuestionButton
          variant="contained"
          onClick={validateAndSave}
          loading={loading}
          saved={saved}
          edited={edited}
          disabled={
            (formState.hasNonXPInternationalFinancialProducts &&
              formState.items.length === 0) ||
            (saved && !edited)
          }
        />
      </div>
    </div>
  )
}
